import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FileText, Upload, Star, Trash2, Download, Pencil, Check, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResumeManagerPage() {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const fetchResumes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/resumes`);
      setResumes(Array.isArray(res.data) ? res.data : []);
    } catch {
      setResumes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchResumes(); }, [fetchResumes]);

  const handleUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["doc", "docx", "pdf"].includes(ext)) {
      alert("Please upload a DOC, DOCX, or PDF file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("resume", file);
      fd.append("display_name", file.name.replace(/\.[^.]+$/, ""));
      await axios.post(`${API}/api/resumes/upload`, fd);
      await fetchResumes();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.patch(`${API}/api/resumes/${id}/set-default`);
      await fetchResumes();
    } catch {
      alert("Failed to set default");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this resume?")) return;
    try {
      await axios.delete(`${API}/api/resumes/${id}`);
      await fetchResumes();
    } catch {
      alert("Delete failed");
    }
  };

  const handleRename = async (id) => {
    if (!editName.trim()) return;
    try {
      await axios.patch(`${API}/api/resumes/${id}/rename`, { display_name: editName.trim() });
      setEditingId(null);
      await fetchResumes();
    } catch {
      alert("Rename failed");
    }
  };

  const handleDownload = (id) => {
    const token = localStorage.getItem("auth_token");
    const resume = resumes.find((r) => r.id === id);
    const fname = resume?.filename || "resume.docx";
    fetch(`${API}/api/resumes/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fname;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert("Download failed"));
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <div className="rm-pg" data-testid="resume-manager-page">
      <div className="rm-hdr">
        <FileText size={26} strokeWidth={1.5} />
        <div>
          <h1 className="rm-h1">Resume Management</h1>
          <p className="rm-sub">Upload, manage, and set your default resume for all features</p>
        </div>
      </div>

      <div
        className={`rm-drop ${dragOver ? "rm-drop-active" : ""}`}
        data-testid="resume-upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => document.getElementById("rm-file-input").click()}
      >
        {uploading ? (
          <><Loader2 size={28} className="rm-spin" /> Uploading...</>
        ) : (
          <><Upload size={28} /> Drop your resume here or click to browse<br /><span className="rm-hint">Supports .docx, .doc, .pdf</span></>
        )}
        <input
          id="rm-file-input"
          type="file"
          accept=".doc,.docx,.pdf"
          style={{ display: "none" }}
          onChange={(e) => handleUpload(e.target.files?.[0])}
          data-testid="resume-file-input"
        />
      </div>

      {loading ? (
        <div className="rm-empty"><Loader2 size={24} className="rm-spin" /> Loading resumes...</div>
      ) : resumes.length === 0 ? (
        <div className="rm-empty" data-testid="resume-empty">No resumes saved yet. Upload your first resume above.</div>
      ) : (
        <div className="rm-tbl-wrap" data-testid="resume-list">
          <table className="rm-tbl">
            <thead>
              <tr>
                <th>Resume Name</th>
                <th>Type</th>
                <th>Upload Date</th>
                <th>Last Modified</th>
                <th>Default</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resumes.map((r) => (
                <tr key={r.id} className={r.is_default ? "rm-row-def" : ""} data-testid={`resume-card-${r.id}`}>
                  <td className="rm-name-cell">
                    {editingId === r.id ? (
                      <div className="rm-edit-row">
                        <input className="rm-edit-in" value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleRename(r.id)} autoFocus data-testid="resume-edit-input" />
                        <button className="rm-icon-btn rm-icon-g" onClick={() => handleRename(r.id)} data-testid="resume-edit-save"><Check size={14} /></button>
                        <button className="rm-icon-btn" onClick={() => setEditingId(null)}><X size={14} /></button>
                      </div>
                    ) : (
                      <div className="rm-name-row">
                        <FileText size={16} className="rm-ficon" />
                        <span>{r.display_name || r.filename}</span>
                        {r.is_default && <span className="rm-def-badge">DEFAULT</span>}
                      </div>
                    )}
                  </td>
                  <td><span className="rm-type">{(r.file_type || "docx").toUpperCase()}</span></td>
                  <td className="rm-date">{formatDate(r.created_at)}</td>
                  <td className="rm-date">{formatDate(r.updated_at)}</td>
                  <td>
                    <button className={`rm-star ${r.is_default ? "rm-star-on" : ""}`} onClick={() => handleSetDefault(r.id)} title={r.is_default ? "Default resume" : "Set as default"} data-testid={`resume-set-default-${r.id}`}>
                      <Star size={18} fill={r.is_default ? "currentColor" : "none"} />
                    </button>
                  </td>
                  <td>
                    <div className="rm-acts">
                      <button className="rm-icon-btn" onClick={() => handleDownload(r.id)} title="Download" data-testid={`resume-download-${r.id}`}><Download size={15} /></button>
                      <button className="rm-icon-btn" onClick={() => { setEditingId(r.id); setEditName(r.display_name || r.filename); }} title="Rename" data-testid={`resume-rename-${r.id}`}><Pencil size={15} /></button>
                      <button className="rm-icon-btn rm-icon-r" onClick={() => handleDelete(r.id)} title="Delete" data-testid={`resume-delete-${r.id}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .rm-pg { max-width: 900px; margin: 0 auto; padding: 24px 16px; }
        .rm-hdr { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; color: var(--text-primary); }
        .rm-h1 { font-size: 1.5rem; font-weight: 700; margin: 0; }
        .rm-sub { font-size: 0.85rem; color: var(--text-secondary); margin: 2px 0 0; }
        .rm-drop { border: 2px dashed var(--border-main); border-radius: 12px; padding: 32px; text-align: center; cursor: pointer; color: var(--text-secondary); font-size: 0.9rem; transition: border-color 0.15s, background 0.15s; margin-bottom: 24px; background: var(--bg-surface); }
        .rm-drop:hover, .rm-drop-active { border-color: var(--primary); background: var(--primary-dim); }
        .rm-hint { font-size: 0.78rem; opacity: 0.7; }
        .rm-spin { animation: rm-sp 1s linear infinite; }
        @keyframes rm-sp { to { transform: rotate(360deg); } }
        .rm-empty { text-align: center; padding: 40px; color: var(--text-secondary); font-size: 0.9rem; display: flex; align-items: center; justify-content: center; gap: 10px; }
        .rm-tbl-wrap { background: var(--bg-surface); border: 1px solid var(--border-main); border-radius: 12px; overflow: hidden; box-shadow: var(--card-shadow); }
        .rm-tbl { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .rm-tbl thead th { text-align: left; padding: 12px 16px; font-weight: 600; font-size: 0.78rem; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid var(--border-main); background: var(--bg-surface-hl); }
        .rm-tbl tbody td { padding: 12px 16px; border-bottom: 1px solid var(--border-main); vertical-align: middle; color: var(--text-primary); }
        .rm-tbl tbody tr:last-child td { border-bottom: none; }
        .rm-row-def { background: var(--primary-dim); }
        .rm-name-cell { min-width: 200px; }
        .rm-name-row { display: flex; align-items: center; gap: 8px; }
        .rm-ficon { color: var(--text-muted); flex-shrink: 0; }
        .rm-def-badge { background: var(--primary); color: #fff; font-size: 0.65rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; letter-spacing: 0.04em; }
        .rm-type { background: var(--bg-surface-hl); border: 1px solid var(--border-main); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; color: var(--text-secondary); }
        .rm-date { color: var(--text-secondary); font-size: 0.82rem; white-space: nowrap; }
        .rm-star { background: none; border: none; cursor: pointer; color: var(--text-muted); padding: 4px; border-radius: 4px; transition: color 0.15s; }
        .rm-star:hover { color: #eab308; }
        .rm-star-on { color: #eab308; }
        .rm-acts { display: flex; gap: 4px; }
        .rm-icon-btn { background: none; border: none; cursor: pointer; color: var(--text-secondary); padding: 6px; border-radius: 6px; transition: background 0.15s, color 0.15s; display: flex; align-items: center; }
        .rm-icon-btn:hover { background: var(--bg-surface-hl); color: var(--text-primary); }
        .rm-icon-r:hover { color: var(--error); }
        .rm-icon-g { color: var(--success); }
        .rm-edit-row { display: flex; align-items: center; gap: 4px; }
        .rm-edit-in { padding: 4px 8px; border: 1px solid var(--primary); border-radius: 6px; font-size: 0.85rem; background: var(--bg-deep); color: var(--text-primary); width: 180px; }
        @media (max-width: 768px) {
          .rm-tbl-wrap { overflow-x: auto; }
          .rm-tbl { min-width: 600px; }
        }
      `}</style>
    </div>
  );
}
